export interface DOMOperationResult {
  success: boolean;
  error?: Error;
}

export const safeInsertBefore = (
  parent: Node,
  newNode: Node,
  referenceNode: Node | null
): DOMOperationResult => {
  try {
    if (!parent) {
      return {
        success: false,
        error: new Error('Parent node is null or undefined')
      };
    }

    if (!newNode) {
      return {
        success: false,
        error: new Error('New node is null or undefined')
      };
    }

    if (referenceNode && referenceNode.parentNode !== parent) {
      return {
        success: false,
        error: new Error('Reference node is not a child of the parent node')
      };
    }

    parent.insertBefore(newNode, referenceNode);
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error(String(error))
    };
  }
};

export const safeAppendChild = (
  parent: Node,
  child: Node
): DOMOperationResult => {
  try {
    if (!parent) {
      return {
        success: false,
        error: new Error('Parent node is null or undefined')
      };
    }

    if (!child) {
      return {
        success: false,
        error: new Error('Child node is null or undefined')
      };
    }

    parent.appendChild(child);
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error(String(error))
    };
  }
};

export const safeRemoveChild = (
  parent: Node,
  child: Node
): DOMOperationResult => {
  try {
    if (!parent) {
      return {
        success: false,
        error: new Error('Parent node is null or undefined')
      };
    }

    if (!child) {
      return {
        success: false,
        error: new Error('Child node is null or undefined')
      };
    }

    if (child.parentNode !== parent) {
      return {
        success: false,
        error: new Error('Child is not a child of the parent node')
      };
    }

    if (!document.body.contains(child)) {
      return {
        success: false,
        error: new Error('Child node is not in the document')
      };
    }

    parent.removeChild(child);
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error(String(error))
    };
  }
};

export const waitForDOMReady = (): Promise<void> => {
  return new Promise((resolve) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
    } else {
      requestAnimationFrame(() => resolve());
    }
  });
};

export const isNodeInDocument = (node: Node): boolean => {
  try {
    return document.body.contains(node);
  } catch {
    return false;
  }
};
